import React, { Dispatch, SetStateAction, useEffect } from 'react';
import cn from 'classnames';
import sortBy from 'lodash/sortBy';
import { useRouter } from 'next/router';
import useReduxSelector from '@/Framework/hooks/useReduxSelector';
import { SCROLL_TO_TABLE_ADDITIONAL_OFFSET_TOP } from '@/finsight/ui/components/strats/constants';
import { IGroupItem, ISidebarAction } from '@/finsight/domain/strats/types/SidebarStrats';

import sidebarStyles from '@/Framework/UI/Organisms/Filters/FiltersPanel/sidebar.scss';
import headerStyles from '@dealroadshow/uikit/core/styles/headers.scss';
import styles from './sidebar.scss';

import StratsSidebarContextProvider, {
  useStratsSidebarContext,
} from '@/finsight/application/Strats/StratsSidebarContext';
import IconFileTable from '@dealroadshow/uikit/core/components/Icon/IconFileTable';
import IconExternalLink from '@dealroadshow/uikit/core/components/Icon/IconExternalLink';
import IconFileTxt2 from '@dealroadshow/uikit/core/components/Icon/IconFileTxt2';
import IconClose from '@dealroadshow/uikit/core/components/Icon/IconClose';

interface ISidebarProps {
  isOpen: boolean,
  triggerOpen: Dispatch<SetStateAction<boolean>>,
}

interface IReduxProps {
  isNotFound: boolean,
}

const stateSelector = (state): IReduxProps => ({
  isNotFound: state.redirect.isNotFound,
});

const Sidebar = ({
  isOpen,
  triggerOpen,
}: ISidebarProps) => {
  const {
    metadataFetching,
    sidebarGroupsFetchig,
    metadata,
    groups,
    viewEdgarFiling,
    getSidebarMetadata,
    getSidebarGroups,
  } = useStratsSidebarContext();
  const { query: { fileId } } = useRouter();
  const { isNotFound } = useReduxSelector(stateSelector);

  useEffect(() => {
    if (!isNotFound) {
      getSidebarMetadata(fileId.toString());
      getSidebarGroups(fileId.toString());
    }
  }, []);

  const isFetching = metadataFetching && sidebarGroupsFetchig;

  const scrollToTable = (id: string | number) => {
    window.scrollTo({
      left: 0,
      top: document.getElementById(`table-${ id }`).offsetTop - SCROLL_TO_TABLE_ADDITIONAL_OFFSET_TOP,
      behavior: 'smooth',
    });
  };

  const renderAction = (action: ISidebarAction) => {
    const { icon: Icon } = action;
    const item = (
      <div
        key={ action.id }
        className={ cn(styles.action, { [styles.withIcon]: action.icon }) }
        onClick={ action.onClick }
      >
        { Icon && (
          <Icon
            className={ cn(styles.icon, {
              [action.className]: action.className,
            }) }
          />
        ) }
        { action.text }
      </div>
    );

    if (action.url) {
      return (
        <a
          key={ action.id }
          href={ action.url }
          /* eslint-disable-next-line react/no-unknown-property */
          download
          target="_blank"
        >
          { item }
        </a>
      );
    }
    return item;
  };

  const renderNavItems = (item: IGroupItem) => renderAction({
    id: item.groupId,
    text: item.groupTitle,
    onClick: () => scrollToTable(item.groupId),
  });

  const sidebarCls = cn(styles.container, {
    [styles.isOpen]: isOpen,
  });

  const sidebarOverlayCls = cn(sidebarStyles.sidebarOverlay, {
    [styles.sidebarOverlayIsOpen]: isOpen,
  });

  const dealDocumentsActions = [
    {
      id: 'data-tape-id',
      text: 'Download Datatape (CSV)',
      icon: IconFileTable,
      url: metadata.dataTapeUrl,
    },
    {
      id: 'view-edgar',
      text: 'View EDGAR Filing',
      icon: IconExternalLink,
      onClick: viewEdgarFiling,
      className: styles.edgarFilingIcon,
    },
  ];

  if (metadata.displayButtonDownloadRelDoc) {
    dealDocumentsActions.splice(1, 0, {
      id: 'rel-doc',
      text: `Download Rel. Doc (${ metadata.dataTapeRelatedDocumentType })`,
      icon: IconFileTxt2,
      url: metadata.dataTapeRelatedDocumentUrl,
    });
  }

  return (
    <>
      <div
        className={ sidebarOverlayCls }
        onClick={ () => triggerOpen(!isOpen) }
      />
      { !isFetching && (
        <div className={ sidebarCls }>
          <div className={ styles.header }>
            <div className={ cn(sidebarStyles.headerTitle, headerStyles.isH2) }>Actions</div>
            <button
              type="button"
              className={ cn(sidebarStyles.headerClose, styles.closeButton) }
              onClick={ () => triggerOpen(!isOpen) }
              data-test="stratsSidebarCloseButton"
            >
              <IconClose />
            </button>
          </div>
          <div className={ styles.content }>
            <div className={ styles.groupHeader }>Deal Documents</div>
            { dealDocumentsActions.map(renderAction) }
            <div className={ styles.groupHeader }>Quick Navigation</div>
            { sortBy(groups, (group) => group.groupTitle)
              .map(renderNavItems) }
          </div>
        </div>
      ) }
    </>
  );
};

export default (props: ISidebarProps) => (
  <StratsSidebarContextProvider>
    <Sidebar { ...props } />
  </StratsSidebarContextProvider>
);
